// RestockReceived (truck stock, BL-203): a restock as the tech received it, where it didn't match what was
// sent, for the office to sort out: a line that came short (12 of 14 elbows), one that came and wasn't sent
// (a flapper), and a supplier's back-order (two hose bibs to Thu 29 Oct). Each is a RecordRow (what
// happened, in words, at our cost for whoever sees our cost) with its choices under it (ChoiceGroup) and
// what the chosen one does, said before it's done. Close says what's still to choose first (ink words after
// StateMark's red bar: red is never small text here), and takes the focus there; closed, it says what was
// decided. The rest came as sent, in one line. The tech reads it: what he told the office, waiting for it.
// Reads only --restock-*.
// New (Domain/Stock) only for its rules (each mismatch needs a choice before Close, what each does said
// first); it draws nothing of its own beyond RecordRow, ChoiceGroup, Notice and Button.
import { useId, useState } from 'react';
import { money } from '../../format.ts';
import { Button } from '../Button/Button.tsx';
import { Choice, ChoiceGroup } from '../ChoiceGroup/ChoiceGroup.tsx';
import { Notice } from '../Notice/Notice.tsx';
import { StateMark } from '../StateMark/StateMark.tsx';
import { RecordList, RecordRow } from '../RecordRow/RecordRow.tsx';
import './RestockReceived.css';

export interface RestockChoice {
  value: string;
  label: string;
  /** What it does, said before it's done ("TR-0419 gets 2 more; TR-0418 closes at 12."). */
  says: string;
}
export interface RestockIssue {
  id: string;
  /** received: a line that came short or wasn't sent; backorder: the supplier's, still on order. */
  kind: 'received' | 'backorder';
  /** What happened: "PEX elbow, ½ in, brass: 14 sent, 12 came". */
  title: string;
  /** Muted, under it: what that means. */
  sub: string;
  /** Its choices' name: "The 2 elbows that didn’t come". */
  label: string;
  choices: RestockChoice[];
  /** Chosen as it opens (a back-order stays on order unless someone cancels it). */
  chosen?: string;
  /** At our cost: only for whoever sees our cost. */
  costCents?: number;
  /** What the tech reads while the office decides ("You collect them Thu 29 Oct, at the counter."). */
  techSays?: string;
}
export interface RestockReceivedProps {
  /** The restock: "TR-0418". */
  refNo: string;
  /** Who made it up and when, and what came as sent, one muted line. */
  head: string;
  issues: RestockIssue[];
  /** "6 more came as sent: Ball valve 4, …". */
  asSent?: string;
  /** Whose van: "Sam". */
  first: string;
  /** The tech's own: read only. */
  readOnly?: boolean;
  initial?: { chosen?: Record<string, string>; tried?: boolean; closed?: boolean };
  onClose?: (chosen: Record<string, string>) => void;
}

export function RestockReceived({
  refNo,
  head,
  issues,
  asSent,
  first,
  readOnly,
  initial = {},
  onClose,
}: RestockReceivedProps) {
  const uid = useId();
  const [chosen, setChosen] = useState<Record<string, string>>({
    ...Object.fromEntries(issues.filter((i) => i.chosen).map((i) => [i.id, i.chosen!])),
    ...initial.chosen,
  });
  const [tried, setTried] = useState(!!initial.tried);
  const [closed, setClosed] = useState(!!initial.closed);
  const missing = issues.filter((i) => !chosen[i.id]);
  const close = () => {
    setTried(true);
    const m = missing[0];
    if (m) {
      document.getElementById(`${uid}-${m.id}`)?.querySelector<HTMLElement>('input')?.focus();
      return;
    }
    setClosed(true);
    onClose?.(chosen);
  };
  const lock = readOnly || closed;
  const said = (i: RestockIssue) => i.choices.find((c) => c.value === chosen[i.id])?.says;
  const issue = (i: RestockIssue) => (
    <div key={i.id} className="fl-restock__issue">
      <RecordRow
        title={i.title}
        sub={i.sub}
        end={i.costCents !== undefined ? money(i.costCents) : undefined}
        endSub={i.costCents !== undefined ? 'at cost' : undefined}
      />
      {lock ? null : (
        <div className="fl-restock__choose" id={`${uid}-${i.id}`}>
          <ChoiceGroup
            label={i.label}
            selectionMode="single"
            value={chosen[i.id] ?? ''}
            onChange={(v) => setChosen((c) => ({ ...c, [i.id]: v }))}
          >
            {i.choices.map((c) => (
              <Choice key={c.value} value={c.value}>
                {c.label}
              </Choice>
            ))}
          </ChoiceGroup>
          {tried && !chosen[i.id] ? (
            <span className="fl-restock__bad">
              <StateMark state="problem" />
              Choose one
            </span>
          ) : null}
        </div>
      )}
      {readOnly ? (
        i.techSays ? (
          <p className="fl-restock__wait">{i.techSays}</p>
        ) : null
      ) : said(i) ? (
        <p className="fl-restock__says">{said(i)}</p>
      ) : null}
    </div>
  );
  const got = issues.filter((i) => i.kind === 'received');
  const back = issues.filter((i) => i.kind === 'backorder');
  return (
    <div className="fl-restock">
      {closed ? (
        <Notice tone="done" takesFocus={!initial.closed} title={`${refNo} is closed.`}>
          <p>
            {issues
              .map((i) => said(i))
              .filter(Boolean)
              .join(' ')}
          </p>
        </Notice>
      ) : null}
      <p className="fl-restock__muted">{head}</p>
      {got.length ? <RecordList label="What didn’t match">{got.map(issue)}</RecordList> : null}
      {asSent ? <p className="fl-restock__muted">{asSent}</p> : null}
      {back.length ? (
        <>
          <h3 className="fl-restock__h">Back-ordered</h3>
          <RecordList label="Back-ordered">{back.map(issue)}</RecordList>
        </>
      ) : null}
      {lock ? null : (
        <div className="fl-restock__foot">
          <Button variant="primary" onPress={close}>
            Close {refNo}
          </Button>
          <span className="fl-restock__muted">{first} sees what you decided on the Truck tab.</span>
          {tried && missing[0] ? (
            <span className="fl-restock__problem" role="alert">
              <StateMark state="problem" />
              Say what happens to {missing[0].label.replace(/^The /, 'the ')} first.
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}
